import React from 'react';
import { 
  Shield, 
  Brain, 
  Zap, 
  Eye, 
  Settings, 
  BarChart3,
  Clock,
  CheckSquare
} from 'lucide-react';

const Features: React.FC = () => {
  const features = [
    {
      icon: <Brain className="h-8 w-8" />,
      title: "AI-Powered Analysis",
      description: "Leverage AWS Bedrock and machine learning to intelligently analyze your security posture with contextual recommendations.",
      color: "text-purple-600"
    },
    {
      icon: <Eye className="h-8 w-8" />,
      title: "Continuous Monitoring",
      description: "Real-time security monitoring using AWS Config, CloudTrail, and EventBridge for immediate threat detection.",
      color: "text-blue-600"
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Automated Remediation",
      description: "Three-tier automated response system from safe auto-fixes to guided manual remediation with AI assistance.",
      color: "text-yellow-600"
    },
    {
      icon: <CheckSquare className="h-8 w-8" />,
      title: "Compliance Automation",
      description: "Built-in support for CIS AWS Foundations, SOC 2, PCI DSS, HIPAA, GDPR, and custom compliance frameworks.",
      color: "text-green-600"
    },
    {
      icon: <Settings className="h-8 w-8" />,
      title: "AWS Well-Architected",
      description: "Complete integration with AWS Well-Architected Framework for comprehensive security pillar assessment.",
      color: "text-indigo-600"
    },
    {
      icon: <BarChart3 className="h-8 w-8" />,
      title: "Risk-Based Prioritization",
      description: "Intelligent risk scoring that considers business impact, threat landscape, and environmental factors.",
      color: "text-red-600"
    },
    {
      icon: <Clock className="h-8 w-8" />,
      title: "Real-Time Alerting",
      description: "Smart alerting with AI-powered noise reduction and multi-channel notifications (Slack, email, webhooks).",
      color: "text-cyan-600"
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Native AWS Integration",
      description: "Deep integration with 40+ AWS security services for comprehensive coverage and seamless deployment.",
      color: "text-orange-600"
    }
  ];

  return (
    <section id="features" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Comprehensive AWS Security Platform
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Transform your AWS security operations with intelligent automation, 
            proactive monitoring, and AI-powered insights that prevent incidents before they happen.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow card-hover"
            >
              <div className={`${feature.color} mb-4`}>
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Additional Feature Highlights */}
        <div className="mt-20">
          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-sm">
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              <div className="lg:col-span-2 space-y-6">
                <h3 className="text-3xl font-bold text-gray-900">
                  Built for AWS, Powered by AI
                </h3>
                <p className="text-lg text-gray-600">
                  Unlike generic multi-cloud tools, AWS Security Guardian is purpose-built for AWS 
                  environments. Our platform leverages AWS Bedrock for intelligent analysis, 
                  OpenSearch for semantic search, and native AWS services for seamless integration.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <CheckSquare className="h-5 w-5 text-green-500" />
                    <span>100% AWS Native Architecture</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckSquare className="h-5 w-5 text-green-500" />
                    <span>One-Click Deployment</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckSquare className="h-5 w-5 text-green-500" />
                    <span>Cross-Account Security Analysis</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckSquare className="h-5 w-5 text-green-500" />
                    <span>Infrastructure-as-Code Integration</span>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-xl p-6">
                <div className="text-center space-y-4">
                  <div className="text-3xl font-bold text-primary-600">40+</div>
                  <div className="text-sm text-gray-600">AWS Security Services Integrated</div>
                </div>
                <div className="mt-6 space-y-3">
                  <div className="bg-white rounded-lg p-3 text-sm">
                    <div className="flex justify-between">
                      <span>Config Rules</span>
                      <span className="text-green-600 font-medium">✓ Active</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-sm">
                    <div className="flex justify-between">
                      <span>GuardDuty</span>
                      <span className="text-green-600 font-medium">✓ Active</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-sm">
                    <div className="flex justify-between">
                      <span>Security Hub</span>
                      <span className="text-green-600 font-medium">✓ Active</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
